import { useState, useMemo, useEffect } from 'react'
import { MessageSquare, CheckCircle, XCircle, AlertCircle, Mail, ChevronDown, ChevronUp, Send, X, Search, Inbox, SendHorizontal, Eye, MousePointer } from 'lucide-react'
import { useInboxResponses, useInboxStats, useUpdateResponse, useSendReply, useSentEmails } from '@hooks/useInbox'
import { InboxResponse } from '@/types'
import { cn } from '@lib/utils'
import { useUIStore } from '@stores/ui'

// mm-full-inbox-20260818: posiljalac se cuva u notes polju kao JSON kad poruka nema klijenta
// mm-reply-visible-20260821: cita trag koji ruta za odgovor upisuje u notes
function repliedAtOf(r: any): string {
  try {
    const n = JSON.parse(r?.notes || '{}')
    return typeof n.repliedAt === 'string' ? n.repliedAt : ''
  } catch {
    return ''
  }
}

function senderOf(r: any): string {
  const direct = r?.client?.email
  if (direct) return direct
  try {
    const n = JSON.parse(r?.notes || '{}')
    return typeof n.from === 'string' ? n.from : ''
  } catch {
    return ''
  }
}

type TabType = 'responses' | 'sent'
type FilterType = 'all' | 'unhandled' | 'positive' | 'negative' | 'auto'

// mm-pagination-20260821
const PAGE_SIZE = 50
type SentFilterType = 'all' | 'opened' | 'clicked' | 'bounced' | 'failed'

export function InboxPage() {
  const [activeTab, setActiveTab] = useState<TabType>('responses')
  const [filter, setFilter] = useState<FilterType>('all')
  const [sentFilter, setSentFilter] = useState<SentFilterType>('all')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [noteInputs, setNoteInputs] = useState<Record<number, string>>({})
  const [replyInputs, setReplyInputs] = useState<Record<number, string>>({})
  const [showReply, setShowReply] = useState<Record<number, boolean>>({})
  // mm-pagination-20260821: strana po tabu
  const [page, setPage] = useState(1)
  const [sentPage, setSentPage] = useState(1)

  const filters = (() => {
    switch (filter) {
      case 'unhandled': return { handled: false }
      case 'positive': return { isPositive: true }
      case 'negative': return { isPositive: false }
      default: return {}
    }
  })()

  const { data: statsData, isLoading: statsLoading } = useInboxStats()
  const { data: responsesData, isLoading: responsesLoading } = useInboxResponses({ ...filters, limit: 1000 })
  const { data: sentData, isLoading: sentLoading } = useSentEmails({
    status: sentFilter === 'all' ? undefined : sentFilter,
    // mm-pagination-20260821: prava server-side paginacija
    limit: PAGE_SIZE,
    offset: (sentPage - 1) * PAGE_SIZE
  })
  const updateResponse = useUpdateResponse()
  const sendReply = useSendReply()
  const showToast = useUIStore((s) => s.showToast) // mm-reply-visible-20260821

  const sentEmails = sentData?.data ?? []

  const responses = responsesData?.data ?? []
  const filteredResponses = useMemo(() => {
    let result = filter === 'auto'
      ? responses.filter(r => r.isAutoReply)
      : responses
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(r =>
        // mm-full-inbox-20260818: client moze biti null kod poruka bez para
        (r.client?.email ?? '').toLowerCase().includes(q) ||
        (r.client?.fullName ?? '').toLowerCase().includes(q) ||
        (r.client?.company ?? '').toLowerCase().includes(q) ||
        r.subject.toLowerCase().includes(q)
      )
    }
    return result
  }, [responses, filter, search])

  // mm-pagination-20260821: Responses se stranici na klijentu da pretraga
  // i dalje gleda sve povucene zapise, ne samo tekucu stranu.
  const pagedResponses = useMemo(
    () => filteredResponses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredResponses, page]
  )

  useEffect(() => { setPage(1) }, [filter, search])
  useEffect(() => { setSentPage(1) }, [sentFilter])

  const stats = statsData

  const handleMarkHandled = (id: number, handled: boolean) => {
    updateResponse.mutate({ id, data: { handled } })
  }

  const handleClassify = (id: number, isPositive: boolean) => {
    updateResponse.mutate({ id, data: { isPositive, handled: true } })
  }

  const handleSaveNotes = (id: number) => {
    const notes = noteInputs[id]
    if (notes !== undefined) {
      updateResponse.mutate({ id, data: { notes } })
    }
  }

  const handleSendReply = (id: number, originalSubject: string) => {
    const body = replyInputs[id]
    if (!body?.trim()) return
    const subject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`
    sendReply.mutate(
      { id, data: { subject, body } },
      {
        onSuccess: (res: any) => {
          setReplyInputs(prev => ({ ...prev, [id]: '' }))
          setShowReply(prev => ({ ...prev, [id]: false }))
          // mm-reply-visible-20260821
          showToast('Reply sent to ' + (res?.sentTo || 'recipient'), 'success')
        },
        // mm-reply-visible-20260821: bez ovoga je svaki neuspeh prolazio nemo.
        // 403 i 429 odbija middleware pre rute, pa nema ni traga u logu backenda.
        onError: (err: any) => {
          const status = err?.response?.status
          const msg = err?.response?.data?.error || err?.message || 'Unknown error'
          showToast('Reply failed' + (status ? ' (' + status + ')' : '') + ': ' + msg, 'error')
        },
      }
    )
  }

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare size={32} className="text-primary" />
        <h1 className="text-3xl font-bold text-foreground">Inbox</h1>
      </div>

      {/* Main Tabs: Responses / Sent */}
      <div className="flex gap-4 border-b border-border">
        <button
          onClick={() => setActiveTab('responses')}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === 'responses'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <Inbox size={18} />
          Responses
        </button>
        <button
          onClick={() => setActiveTab('sent')}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === 'sent'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <SendHorizontal size={18} />
          Sent
        </button>
      </div>

      {activeTab === 'responses' ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label="Total" value={stats?.total ?? 0} icon={<Mail size={20} />} color="blue" loading={statsLoading} />
            <StatCard label="Unhandled" value={stats?.unhandled ?? 0} icon={<AlertCircle size={20} />} color="amber" loading={statsLoading} />
            <StatCard label="Positive" value={stats?.positive ?? 0} icon={<CheckCircle size={20} />} color="green" loading={statsLoading} />
            <StatCard label="Negative" value={stats?.negative ?? 0} icon={<XCircle size={20} />} color="red" loading={statsLoading} />
            <StatCard label="Auto-replies" value={stats?.autoReplies ?? 0} icon={<Mail size={20} />} color="gray" loading={statsLoading} />
          </div>

          {/* Search + Filter Tabs */}
          <div className="space-y-3">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, email, company, subject..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-input bg-background py-2 pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="flex gap-2 border-b border-border pb-2">
              {([
                ['all', 'All'],
                ['unhandled', 'Unhandled'],
                ['positive', 'Positive'],
                ['negative', 'Negative'],
                ['auto', 'Auto-replies'],
              ] as [FilterType, string][]).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    filter === key
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Responses Table */}
          {responsesLoading ? (
            <div className="py-12 text-center text-muted-foreground">Loading responses...</div>
          ) : filteredResponses.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No responses found</div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Client</th>
                    {/* mm-inbox-mbcol-20260818 */}
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Mailbox</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Received</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Sentiment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Actions</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pagedResponses.map((r) => (
                    <ResponseRow
                      key={r.id}
                      response={r}
                      expanded={expandedId === r.id}
                      onToggle={() => toggleExpand(r.id)}
                      onMarkHandled={handleMarkHandled}
                      onClassify={handleClassify}
                      noteValue={noteInputs[r.id] ?? r.notes ?? ''}
                      onNoteChange={(val) => setNoteInputs(prev => ({ ...prev, [r.id]: val }))}
                      onSaveNotes={() => handleSaveNotes(r.id)}
                      replyValue={replyInputs[r.id] ?? ''}
                      onReplyChange={(val) => setReplyInputs(prev => ({ ...prev, [r.id]: val }))}
                      showReply={showReply[r.id] ?? false}
                      onToggleReply={() => setShowReply(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                      onSendReply={() => handleSendReply(r.id, r.subject)}
                      replySending={sendReply.isPending}
                      formatDate={formatDate}
                    />
                  ))}
                </tbody>
              </table>
              {/* mm-pagination-20260821 */}
              <PageBar
                page={page}
                pageSize={PAGE_SIZE}
                total={filteredResponses.length}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => p + 1)}
              />
            </div>
          )}
        </>
      ) : (
        <>
          {/* Sent Emails Tab */}
          <div className="flex gap-2 border-b border-border pb-2">
            {([
              ['all', 'All Sent'],
              ['opened', 'Opened'],
              ['clicked', 'Clicked'],
              ['bounced', 'Bounced'],
              ['failed', 'Failed'],
            ] as [SentFilterType, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSentFilter(key)}
                className={cn(
                  'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                  sentFilter === key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Sent Emails Table */}
          {sentLoading ? (
            <div className="py-12 text-center text-muted-foreground">Loading sent emails...</div>
          ) : sentEmails.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No sent emails found</div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Recipient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Subject</th>
                    {/* mm-inbox-session-20260818:mailbox-col */}
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Mailbox</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Sent At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Tracking</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sentEmails.map((email: any) => (
                    <tr key={email.id} className="hover:bg-accent/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{email.client?.fullName || '—'}</div>
                        <div className="text-xs text-muted-foreground">{email.client?.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-foreground">
                        {/* mm-reply-visible-20260821: odgovori nose sequenceStep 0 */}
                        {email.sequenceStep === 0 && (
                          <span className="mr-2 inline-flex items-center rounded-full bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-300">
                            Reply
                          </span>
                        )}
                        {email.subject}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-secondary-foreground">{email.mailbox?.name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{email.mailbox?.email || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(email.sentAt)}</td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                          email.status === 'SENT' && 'bg-green-900/40 text-green-300',
                          email.status === 'BOUNCED' && 'bg-red-900/40 text-red-300',
                          email.status === 'FAILED' && 'bg-red-900/40 text-red-300',
                          email.status === 'PENDING' && 'bg-amber-900/40 text-amber-300'
                        )}>
                          {email.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 text-xs">
                          {email.openedAt && (
                            <span className="flex items-center gap-1 text-blue-400" title={`Opened: ${formatDate(email.openedAt)}`}>
                              <Eye size={14} /> Opened
                            </span>
                          )}
                          {email.clickedAt && (
                            <span className="flex items-center gap-1 text-green-400" title={`Clicked: ${formatDate(email.clickedAt)}`}>
                              <MousePointer size={14} /> Clicked
                            </span>
                          )}
                          {!email.openedAt && !email.clickedAt && (
                            <span className="text-muted-foreground">No activity</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* mm-pagination-20260821 */}
              <PageBar
                page={sentPage}
                pageSize={PAGE_SIZE}
                total={sentData?.pagination?.total ?? 0}
                onPrev={() => setSentPage((p) => Math.max(1, p - 1))}
                onNext={() => setSentPage((p) => p + 1)}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function StatCard({ label, value, icon, color, loading }: {
  label: string
  value: number
  icon: React.ReactNode
  color: 'blue' | 'green' | 'red' | 'amber' | 'gray'
  loading: boolean
}) {
  const colorClasses = {
    blue: 'bg-blue-900/30 text-blue-300 border-blue-800/50',
    green: 'bg-green-900/30 text-green-300 border-green-800/50',
    red: 'bg-red-900/30 text-red-300 border-red-800/50',
    amber: 'bg-amber-900/30 text-amber-300 border-amber-800/50',
    gray: 'bg-muted text-muted-foreground border-border',
  }

  return (
    <div className={cn('rounded-lg border p-4', colorClasses[color])}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        {icon}
      </div>
      <p className="mt-2 text-2xl font-bold">
        {loading ? '...' : value}
      </p>
    </div>
  )
}

function ResponseRow({ response, expanded, onToggle, onMarkHandled, onClassify, noteValue, onNoteChange, onSaveNotes, replyValue, onReplyChange, showReply, onToggleReply, onSendReply, replySending, formatDate }: {
  response: InboxResponse
  expanded: boolean
  onToggle: () => void
  onMarkHandled: (id: number, handled: boolean) => void
  onClassify: (id: number, isPositive: boolean) => void
  noteValue: string
  onNoteChange: (val: string) => void
  onSaveNotes: () => void
  replyValue: string
  onReplyChange: (val: string) => void
  showReply: boolean
  onToggleReply: () => void
  onSendReply: () => void
  replySending: boolean
  formatDate: (d: string) => string
}) {
  return (
    <>
      <tr
        className={cn('cursor-pointer hover:bg-accent/50', expanded && 'bg-accent/50')}
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <div className="font-medium text-foreground">
            {response.client?.fullName || senderOf(response) || '\u2014'}
          </div>
          <div className="text-xs text-muted-foreground">
            {response.client?.email || (response.client ? '' : 'bez para sa poslatim')}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">
          <div className="text-secondary-foreground">{response.mailbox?.name || '—'}</div>
          <div className="text-xs text-muted-foreground">{response.mailbox?.email || ''}</div>
        </td>
        <td className="px-4 py-3 text-sm text-secondary-foreground">{response.subject}</td>
        <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(response.receivedAt)}</td>
        <td className="px-4 py-3">
          {response.handled ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-900/40 px-2 py-0.5 text-xs font-medium text-green-300">
              <CheckCircle size={12} /> Handled
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-300">
              <AlertCircle size={12} /> Pending
            </span>
          )}
          {repliedAtOf(response) && (
            <span
              className="ml-1 inline-flex items-center gap-1 rounded-full bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-300"
              title={"Odgovoreno " + repliedAtOf(response).slice(0, 19).replace('T', ' ')}
            >
              Replied
            </span>
          )}
          {response.isAutoReply && (
            <span className="ml-1 inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              Auto
            </span>
          )}
        </td>
        <td className="px-4 py-3">
          {response.isPositive === true && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-900/40 px-2 py-0.5 text-xs font-medium text-green-300">
              <CheckCircle size={12} /> Positive
            </span>
          )}
          {response.isPositive === false && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-900/40 px-2 py-0.5 text-xs font-medium text-red-300">
              <XCircle size={12} /> Negative
            </span>
          )}
          {response.isPositive === null && (
            <span className="text-xs text-muted-foreground">Unclassified</span>
          )}
        </td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1">
            {!response.handled && (
              <button
                onClick={() => onMarkHandled(response.id, true)}
                className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700"
              >
                Handle
              </button>
            )}
            {response.handled && (
              <button
                onClick={() => onMarkHandled(response.id, false)}
                className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
              >
                Unhandle
              </button>
            )}
            {response.isPositive !== true && (
              <button
                onClick={() => onClassify(response.id, true)}
                className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                title="Mark positive"
              >
                +
              </button>
            )}
            {response.isPositive !== false && (
              <button
                onClick={() => onClassify(response.id, false)}
                className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                title="Mark negative"
              >
                -
              </button>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-center text-muted-foreground">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-muted/30 px-6 py-4" onClick={(e) => e.stopPropagation()}>
            <div className="space-y-4">
              {/* Close button */}
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase text-muted-foreground">Email Body</h4>
                <button
                  onClick={onToggle}
                  className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-border bg-card p-3 text-sm text-foreground">
                {response.body}
              </pre>

              {/* Notes */}
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground">Notes</h4>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    value={noteValue}
                    onChange={(e) => onNoteChange(e.target.value)}
                    placeholder="Add notes..."
                    className="flex-1 rounded border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <button
                    onClick={onSaveNotes}
                    className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90"
                  >
                    Save
                  </button>
                </div>
              </div>

              {/* Reply */}
              <div>
                {!showReply ? (
                  <button
                    onClick={onToggleReply}
                    className="flex items-center gap-2 rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    <Send size={14} />
                    Reply
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                        Reply to {response.client?.email || senderOf(response) || '—'}
                      </h4>
                      <button
                        onClick={onToggleReply}
                        className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <textarea
                      value={replyValue}
                      onChange={(e) => onReplyChange(e.target.value)}
                      placeholder="Write your reply..."
                      rows={4}
                      className="w-full rounded border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={onSendReply}
                        disabled={replySending || !replyValue?.trim()}
                        className="flex items-center gap-2 rounded bg-green-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        <Send size={14} />
                        {replySending ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// mm-pagination-20260821
function PageBar({ page, pageSize, total, onPrev, onNext }: {
  page: number
  pageSize: number
  total: number
  onPrev: () => void
  onNext: () => void
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  const last = Math.max(1, Math.ceil(total / pageSize))
  return (
    <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm">
      <span className="text-muted-foreground">
        {total === 0 ? 'Nema zapisa' : `Prikazano ${from}\u2013${to} od ${total}`}
      </span>
      <div className="flex items-center gap-2">
        <button
          onClick={onPrev}
          disabled={page <= 1}
          className="rounded border border-border px-3 py-1 text-sm text-foreground hover:bg-accent disabled:opacity-40"
        >
          Prethodna
        </button>
        <span className="text-muted-foreground">{page} / {last}</span>
        <button
          onClick={onNext}
          disabled={page >= last}
          className="rounded border border-border px-3 py-1 text-sm text-foreground hover:bg-accent disabled:opacity-40"
        >
          Sledeca
        </button>
      </div>
    </div>
  )
}
